import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import PromptInputForAssessment from '../common/ui/PromptInputForAssessment';
import api from '../../services/api';
import { jobService } from '../../services/job.service';
import { toast } from 'react-toastify';
import AudioInterviewStage from '../audio/AudioInterviewStage';
import AgentInterviewStage from '../audio/AgentInterviewStage';

const PsychoanalyticalTest = () => {
    const params = useParams();
    const { jobId, candidateId } = params;
    const navigate = useNavigate();


    // Chat conversation state
    const [conversationHistory, setConversationHistory] = useState([]);
    const [userAnswer, setUserAnswer] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isCompleted, setIsCompleted] = useState(false);
    const [finalResult, setFinalResult] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(20 * 60); // 20 minutes in seconds
    const [timerStarted, setTimerStarted] = useState(false);
    const [maxQuestions] = useState(20); // Updated to 20 questions
    const [companyName, setCompanyName] = useState('');
    const [modality, setModality] = useState('audio');
    const [firstAiLine, setFirstAiLine] = useState('');

    // Start interview by calling the psychoanalytical API
    const startInterview = useCallback(async () => {
        try {
            setIsLoading(true);
            console.log('Starting psychoanalytical interview for:', { jobId, candidateId });
            
            const response = await api.get(`/jobs/${jobId}/psychoanalytical/start/${candidateId}`);
            
            if (response.data.success) {
                const { question, modality: m } = response.data.data;
                if (m && m !== 'inherit') setModality(m);
                setFirstAiLine(question || '');

                // Add first question to conversation history
                setConversationHistory([{
                    type: 'question',
                    text: question,
                    timestamp: new Date()
                }]);

                // Start the timer when interview begins
                setTimerStarted(true);

                toast.success('Interview started successfully!');
            }
        } catch (error) {
            console.error("Error starting interview:", error);
            toast.error("Failed to start interview. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [jobId, candidateId]);

    useEffect(() => {
        // Auto-start interview when component loads
        startInterview();
    }, [jobId, candidateId, startInterview]);

    // Fetch job and company information
    useEffect(() => {
        const fetchJobInfo = async () => {
            try {
                const response = await jobService.getJobTitle(jobId);
                if (response.companyName) {
                    setCompanyName(response.companyName);
                }
            } catch (error) {
                console.error('Error fetching job info:', error);
            }
        };
        
        if (jobId) {
            fetchJobInfo();
        }
    }, [jobId]);

    // Timer logic
    useEffect(() => {
        let timer;
        
        if (timerStarted && !isCompleted) {
            timer = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        // Auto-submit when timer reaches zero
                        handleAutoSubmit();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [timerStarted, isCompleted]);

    // Auto-submit function
    const handleAutoSubmit = async () => {
        if (isSubmitting || isCompleted) return;
        
        try {
            setIsSubmitting(true);
            toast.info("Time's up! Submitting your assessment automatically...");
            
            // Submit final response if there's any pending answer
            if (userAnswer.trim()) {
                await handleSubmitAnswer();
            }
            
            // Force assessment completion
            setIsCompleted(true);
            setFinalResult({
                score: 0,
                feedback: 'Assessment ended due to time limit'
            });
            
            setTimeout(() => {
                navigate('/test-complete', { 
                    state: { 
                        assessment: { overallScore: 0, feedback: 'Assessment ended due to time limit' },
                        type: 'psychoanalytical'
                    }
                });
            }, 2000);
        } catch (error) {
            console.error("Error in auto-submit:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Format time function
    const formatTime = (timeInSeconds) => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = timeInSeconds % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    // Proctoring: Prevent tab changes and browser navigation
    useEffect(() => {
        if (!timerStarted || isCompleted) return;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = 'Are you sure you want to leave? Your assessment progress will be lost.';
            return e.returnValue;
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                toast.warning('⚠️ Please stay on this tab during the assessment. Tab switching is not allowed.');
                // Optionally auto-submit if they switch tabs
                // handleAutoSubmit();
            }
        };

        const handleKeyDown = (e) => {
            // Prevent common keyboard shortcuts for tab switching
            if (e.ctrlKey || e.metaKey) {
                if (e.key === 'Tab' || e.key === 't' || e.key === 'w') {
                    e.preventDefault();
                    toast.warning('⚠️ Tab switching is not allowed during the assessment.');
                }
            }
        };

        // Add event listeners
        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('keydown', handleKeyDown);

        // Cleanup
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [timerStarted, isCompleted]);

    const handleSubmitAnswer = async () => {
        if (!userAnswer.trim()) return;
        
        try {
            setIsSubmitting(true);
            
            // Add user's answer to conversation
            const newUserMessage = {
                type: 'answer',
                text: userAnswer,
                timestamp: new Date()
            };
            setConversationHistory(prev => [...prev, newUserMessage]);
            
            console.log('Submitting answer:', userAnswer);
            
            const response = await api.post(
                `/jobs/${jobId}/psychoanalytical/continue/${candidateId}`,
                { answer: userAnswer }
            );
            
            if (response.data.success) {
                if (response.data.data.isCompleted) {
                    // Interview completed
                    const { score, feedback } = response.data.data;
                    setIsCompleted(true);
                    setFinalResult({ score, feedback });
                    toast.success(`Interview completed! Score: ${score}/100`);
                    
                    setTimeout(() => {
                        navigate('/test-complete', { 
                            state: { 
                                assessment: { overallScore: score, feedback },
                                type: 'psychoanalytical'
                            }
                        });
                    }, 2000);
                } else {
                    // Continue with next question
                    const { question } = response.data.data;
                    setConversationHistory(prev => [...prev, {
                        type: 'question',
                        text: question,
                        timestamp: new Date()
                    }]);
                }
                
                // Clear user input
                setUserAnswer("");
            }
        } catch (error) {
            console.error("Error submitting answer:", error);
            toast.error("Failed to submit answer. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };



    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-white to-red-200">
                <div className="text-center">
                    <div className="relative w-20 h-20 mx-auto mb-4">
                        <div className="w-20 h-20 rounded-full border-4 border-gray-200"></div>
                        <div className="absolute top-0 left-0 w-20 h-20 rounded-full border-4 border-[#F97B5C] border-t-transparent animate-spin"></div>
                    </div>
                    <p className="text-gray-600 text-lg">Starting your psychoanalytical assessment...</p>
                </div>
            </div>
        );
    }

    if (isCompleted && finalResult) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-white to-green-200">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-4 text-center">
                    <div className="text-green-500 text-6xl mb-4">✅</div>
                    <h2 className="text-2xl font-semibold text-green-600 mb-4">
                        Assessment Completed!
                    </h2>
                    <p className="text-gray-600 mb-4">
                        Score: <span className="font-bold text-green-600">{finalResult?.score || 0}/100</span>
                    </p>
                    {finalResult.feedback && (
                        <p className="text-gray-600 text-sm">
                            {finalResult?.feedback || ''}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen relative bg-gradient-to-b from-white to-red-200">
            {/* Header */}
            <div className="bg-white shadow-sm border-b px-6 py-4">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">
                                {companyName ? `${companyName}.ai` : 'Company.ai'}
                            </h1>
                            <p className="text-gray-600">Psychoanalytical Assessment</p>
                        </div>
                        {timerStarted && (
                            <div className={`text-lg font-semibold ${timeRemaining < 300 ? 'text-red-600 animate-pulse' : 'text-blue-600'}`}>
                                ⏰ {formatTime(timeRemaining)}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Audio mode — ElevenLabs Conversational AI agent (Gemini-driven, low latency) */}
            {modality === 'audio' && !isCompleted && (
              <div className="flex-1 max-w-4xl mx-auto w-full p-6">
                <AgentInterviewStage
                  round="psychoanalytical"
                  jobId={jobId}
                  resumeId={candidateId}
                  candidateName={companyName || 'there'}
                  onComplete={(result) => {
                    if (result?.aborted) return;
                    setIsCompleted(true);
                    setFinalResult({ score: result?.overallScore, feedback: result?.recommendation });
                    setTimeout(() => navigate('/test-complete', {
                      state: { assessment: { overallScore: result?.overallScore, feedback: result?.recommendation }, type: 'psychoanalytical' }
                    }), 1800);
                  }}
                />
              </div>
            )}

            {/* Text mode (legacy) */}
            {modality !== 'audio' && (
            <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full">
                {/* Chat Messages */}
                <div className="flex-1 p-6 overflow-y-auto">
                    <div className="space-y-4 max-w-4xl mx-auto">
                        {conversationHistory.map((msg, index) => (
                            <div key={index} className={`flex ${msg.type === 'answer' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-3xl px-6 py-4 rounded-2xl shadow-sm ${
                                    msg.type === 'answer' 
                                        ? 'bg-blue-500 text-white' 
                                        : 'bg-white text-gray-800'
                                }`}>
                                    <div className="text-sm font-medium mb-2">
                                        {msg.type === 'answer' ? 'You' : companyName || 'Company'}
                                    </div>
                                    <div className="text-base whitespace-pre-wrap leading-relaxed break-words">{msg.text}</div>
                                    <div className="text-xs opacity-70 mt-2">
                                        {msg.timestamp.toLocaleTimeString()}
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        {/* Loading indicator */}
                        {isSubmitting && (
                            <div className="flex justify-start">
                                <div className="bg-white text-gray-800 px-6 py-4 rounded-2xl shadow-sm max-w-3xl">
                                    <div className="flex items-center gap-3">
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                                        <span className="text-sm">Processing your response...</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Input Area */}
                <div className="border-t bg-white p-6">
                    <div className="max-w-4xl mx-auto">
                        <PromptInputForAssessment
                            value={userAnswer}
                            onChange={setUserAnswer}
                            onSubmit={handleSubmitAnswer}
                            loading={isSubmitting}
                            placeholder="Type your response here..."
                            showSaveButton={true}
                            saveButtonText={
                                isCompleted
                                    ? "Completed"
                                    : isSubmitting
                                    ? "Submitting..."
                                    : "Submit Response"
                            }
                            disabled={isSubmitting || isCompleted}
                        />
                    </div>
                </div>
            </div>
            )}

            {/* Completion message */}
            {isCompleted && finalResult && (
                <div className="fixed bottom-0 left-0 right-0 bg-green-50 border-t border-green-200">
                    <div className="max-w-7xl mx-auto p-6">
                        <div className="text-center">
                            <h3 className="text-lg font-semibold text-green-800 mb-2">
                                Assessment Completed Successfully!
                            </h3>
                            <p className="text-green-600">
                                {finalResult.score !== null ? `Score: ${finalResult.score}/100` : ''} | 
                                {finalResult.feedback}
                            </p>
                            <p className="text-sm text-gray-600 mt-2">
                                Thank you for your time. We'll review your responses and get back to you soon.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PsychoanalyticalTest;